import React, {useState, useEffect, useRef} from 'react';
import styles from '../tables.module.css';
import localStyles from './ReferralsTable.module.css';
import {PRISMAX_BACKEND_URL} from "../../../configs/config";
import {downloadCsv, toUtcIso} from './shared/downloadCsv';

const STATUS_LABELS = {
    new: 'New',
    followed_up: 'Followed up',
    contract_signed: 'Contract signed',
    not_eligible: 'Not eligible',
    no_deal: 'No deal',
};

const PAYOUT_LABELS = {
    unpaid: 'Unpaid',
    paid: 'Paid',
};

const LIST_ENDPOINT = `${PRISMAX_BACKEND_URL}/api/admin/get-marketing-referrals`;
const UPDATE_ENDPOINT = `${PRISMAX_BACKEND_URL}/api/admin/marketing-referrals`;
const COLUMN_COUNT = 13;

const authHeaders = () => ({
    "Authorization": `Bearer ${localStorage.getItem("prismax_admin_token")}`,
    "Content-Type": "application/json",
});

const fullName = (item) => [item.referrer_first_name, item.referrer_last_name].filter(Boolean).join(' ');

const isInternalEmail = (email) => typeof email === 'string' && email.toLowerCase().endsWith('@prismax.ai');

const formatNeeds = (value) => (Array.isArray(value) ? value.join(', ') : value || '');

const isHttpUrl = (value) => typeof value === 'string' && /^https?:\/\//i.test(value);

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : '');

const formatUsd = (value) => (value == null ? '' : `$${Number(value).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`);

const todayIso = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const buildQuery = (page, pageSize, filters) => {
    const params = new URLSearchParams({page: String(page), page_size: String(pageSize)});
    if (filters.status) params.set('status', filters.status);
    if (filters.payout) params.set('payout', filters.payout);
    if (filters.search) params.set('search', filters.search);
    return params.toString();
};

const fetchPage = async (query) => {
    const response = await fetch(`${LIST_ENDPOINT}?${query}`, {headers: authHeaders()});
    if (!response.ok) {
        throw new Error('Failed to fetch referrals');
    }
    const data = await response.json();
    if (!data.success || !data.data) {
        throw new Error('Invalid data format');
    }
    return data;
};

const draftFrom = (item) => ({
    status: item.status,
    paid: Boolean(item.paid_at),
    paidAt: item.paid_at || todayIso(),
    amount: item.payout_amount_usd || '',
    adminNotes: item.admin_notes || '',
});

const ReferralsTable = () => {
    const [referrals, setReferrals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [csvError, setCsvError] = useState('');
    const [downloadingCsv, setDownloadingCsv] = useState(false);
    const [statusFilter, setStatusFilter] = useState('');
    const [payoutFilter, setPayoutFilter] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [editingItem, setEditingItem] = useState(null);
    const [draft, setDraft] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const itemsPerPage = 10;
    const isInitialLoad = useRef(true);

    const filters = {status: statusFilter, payout: payoutFilter, search};

    useEffect(() => {
        const timer = setTimeout(() => {
            setSearch(searchInput.trim());
            setCurrentPage(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchInput]);

    useEffect(() => {
        const fetchReferrals = async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await fetchPage(buildQuery(currentPage, itemsPerPage, {status: statusFilter, payout: payoutFilter, search}));
                setReferrals(data.data);
                setTotalPages(data.total_pages || 1);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
                isInitialLoad.current = false;
            }
        };

        fetchReferrals();
    }, [currentPage, statusFilter, payoutFilter, search]);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const goToPreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const goToNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const onFilterChange = (setter) => (e) => {
        setter(e.target.value);
        setCurrentPage(1);
    };

    const startEdit = (item) => {
        setEditingItem(item);
        setDraft(draftFrom(item));
        setSaveError('');
    };

    const closeEdit = () => {
        if (saving) return;
        setEditingItem(null);
        setDraft(null);
        setSaveError('');
    };

    useEffect(() => {
        if (!editingItem) return undefined;
        const onKeyDown = (e) => {
            if (e.key === 'Escape' && !saving) {
                setEditingItem(null);
                setDraft(null);
                setSaveError('');
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [editingItem, saving]);

    const updateDraft = (changes) => setDraft((d) => ({...d, ...changes}));

    const saveEdit = async () => {
        if (draft.paid && (!draft.paidAt || draft.amount === '')) {
            setSaveError('Enter the paid date and amount.');
            return;
        }
        setSaving(true);
        setSaveError('');
        try {
            const response = await fetch(`${UPDATE_ENDPOINT}/${editingItem.referral_id}`, {
                method: 'PATCH',
                headers: authHeaders(),
                body: JSON.stringify({
                    status: draft.status,
                    paid_at: draft.paid ? draft.paidAt : null,
                    payout_amount_usd: draft.paid ? draft.amount : null,
                    admin_notes: draft.adminNotes.trim() || null,
                }),
            });
            const data = await response.json().catch(() => null);
            if (!response.ok || !data?.success) {
                throw new Error(data?.msg || 'Failed to save referral');
            }
            setReferrals((rows) => rows.map((row) => (row.referral_id === editingItem.referral_id ? {...row, ...data.data} : row)));
            setEditingItem(null);
            setDraft(null);
        } catch (err) {
            setSaveError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const downloadAllCsv = async () => {
        setDownloadingCsv(true);
        setCsvError('');
        try {
            const allRows = [];
            let page = 1;
            let pages = 1;
            do {
                const data = await fetchPage(buildQuery(page, 500, filters));
                allRows.push(...data.data);
                pages = data.total_pages || 1;
                page += 1;
            } while (page <= pages);
            downloadCsv('referrals', [
                'Referral ID', 'Submitted At', 'Expires At', 'Referrer First Name', 'Referrer Last Name', 'Referrer Email',
                'PrismaX Email', 'Company', 'Website', 'Company Domain', 'First For Company', 'Relationship', 'Data Needs',
                'Stage', 'Contact Name', 'Contact Role', 'Notes', 'Status', 'Followed Up At', 'Paid At', 'Payout Amount USD',
                'Admin Notes', 'Updated At', 'Updated By Admin ID', 'IP Address', 'Location',
            ], allRows.map((item) => [
                item.referral_id,
                toUtcIso(item.created_at),
                toUtcIso(item.expires_at),
                item.referrer_first_name,
                item.referrer_last_name,
                item.referrer_email,
                isInternalEmail(item.referrer_email) ? 'Yes' : 'No',
                item.company,
                item.website,
                item.company_domain,
                item.is_first_for_company ? 'Yes' : 'No',
                item.relationship,
                formatNeeds(item.data_needs),
                item.stage,
                item.contact_name,
                item.contact_role,
                item.notes,
                STATUS_LABELS[item.status] || item.status,
                toUtcIso(item.followed_up_at),
                item.paid_at,
                item.payout_amount_usd,
                item.admin_notes,
                toUtcIso(item.updated_at),
                item.updated_by_admin_id,
                item.ip_address,
                item.location,
            ]));
        } catch (err) {
            setCsvError(err.message);
        } finally {
            setDownloadingCsv(false);
        }
    };

    const renderEditModal = () => (
        <div className={styles.modalOverlay} onClick={(e) => { if (e.target === e.currentTarget) closeEdit(); }}>
            <div className={`${styles.modal} ${localStyles.editModal}`} role="dialog" aria-modal="true" aria-labelledby="referral-edit-title">
                <h3 id="referral-edit-title">Edit referral #{editingItem.referral_id}</h3>
                <p className={styles.modalDescription}>
                    {editingItem.company}
                    <br/>
                    Referred by {fullName(editingItem)} ({editingItem.referrer_email})
                </p>

                <div className={styles.formGroup}>
                    <label htmlFor="referral-edit-status">Status</label>
                    <select
                        id="referral-edit-status"
                        className={`${styles.input} ${localStyles.modalSelect}`}
                        value={draft.status}
                        onChange={(e) => updateDraft({status: e.target.value})}
                    >
                        {Object.entries(STATUS_LABELS).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                        ))}
                    </select>
                </div>

                <div className={styles.formGroup}>
                    <label className={localStyles.checkField}>
                        <input
                            type="checkbox"
                            checked={draft.paid}
                            onChange={(e) => updateDraft({paid: e.target.checked})}
                        />
                        Paid
                    </label>
                </div>

                {draft.paid ? (
                    <div className={localStyles.row2}>
                        <div className={styles.formGroup}>
                            <label htmlFor="referral-edit-paid-at">Paid date</label>
                            <input
                                id="referral-edit-paid-at"
                                type="date"
                                className={styles.input}
                                value={draft.paidAt}
                                onChange={(e) => updateDraft({paidAt: e.target.value})}
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="referral-edit-amount">Amount (USD)</label>
                            <input
                                id="referral-edit-amount"
                                type="number"
                                min="0"
                                max="100000"
                                step="0.01"
                                className={styles.input}
                                value={draft.amount}
                                onChange={(e) => updateDraft({amount: e.target.value})}
                            />
                        </div>
                    </div>
                ) : null}

                <div className={styles.formGroup}>
                    <label htmlFor="referral-edit-notes">Admin notes</label>
                    <textarea
                        id="referral-edit-notes"
                        className={`${styles.input} ${styles.textarea} ${localStyles.modalTextarea}`}
                        maxLength={2000}
                        value={draft.adminNotes}
                        onChange={(e) => updateDraft({adminNotes: e.target.value})}
                    />
                </div>

                {saveError ? <div role="alert" className={styles.errorMessage}>{saveError}</div> : null}

                <div className={styles.modalButtons}>
                    <button type="button" className={styles.cancelButton} disabled={saving} onClick={closeEdit}>
                        Cancel
                    </button>
                    <button type="button" className={styles.confirmButton} disabled={saving} onClick={saveEdit}>
                        {saving ? 'Saving…' : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );

    if (loading && isInitialLoad.current) {
        return <div className={styles.loading}>Loading referrals...</div>;
    }

    return (
        <div className={styles.tableContainer}>
            <div className={styles.tableHeaderRow}>
                <h2 className={styles.tableTitle}>Referrals</h2>
                <div className={styles.headerActions}>
                    <button type="button" className={styles.csvButton} disabled={downloadingCsv} onClick={downloadAllCsv}>
                        {downloadingCsv ? 'Preparing…' : 'Download CSV'}
                    </button>
                </div>
            </div>
            <div className={localStyles.filters}>
                <label className={localStyles.filterLabel}>
                    Status
                    <select className={styles.select} value={statusFilter} onChange={onFilterChange(setStatusFilter)}>
                        <option value="">All</option>
                        {Object.entries(STATUS_LABELS).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                        ))}
                    </select>
                </label>
                <label className={localStyles.filterLabel}>
                    Payout
                    <select className={styles.select} value={payoutFilter} onChange={onFilterChange(setPayoutFilter)}>
                        <option value="">All</option>
                        {Object.entries(PAYOUT_LABELS).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                        ))}
                    </select>
                </label>
                <input
                    type="search"
                    className={`${styles.tableInput} ${localStyles.search}`}
                    placeholder="Search company, referrer name or email"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                />
            </div>
            {csvError ? <div role="alert" className={styles.actionError}>{csvError}</div> : null}
            {error ? <div className={styles.error}>Error: {error}</div> : null}
            {!error && (
                <div className={styles.tableWrapper}>
                    <table className={styles.table}>
                        <thead>
                        <tr>
                            <th>ID</th>
                            <th>Submitted</th>
                            <th>Referrer</th>
                            <th>Company</th>
                            <th>Relationship</th>
                            <th>Needs</th>
                            <th>Stage</th>
                            <th>Contact</th>
                            <th>Notes</th>
                            <th>Status</th>
                            <th>Payout</th>
                            <th>IP / Location</th>
                            <th></th>
                        </tr>
                        </thead>
                        <tbody>
                        {referrals.length > 0 ? (
                            referrals.map((item) => (
                                <tr key={item.referral_id}>
                                    <td>{item.referral_id}</td>
                                    <td className={localStyles.nowrap}>
                                        {new Date(item.created_at).toLocaleString()}
                                        <div className={styles.muted}>Expires {formatDate(item.expires_at)}</div>
                                    </td>
                                    <td>
                                        {fullName(item)}
                                        <div className={styles.muted}>
                                            {item.referrer_email}
                                            {isInternalEmail(item.referrer_email) ? <span className={localStyles.tag}>PrismaX</span> : null}
                                        </div>
                                    </td>
                                    <td>
                                        {isHttpUrl(item.website) ? (
                                            <a className={localStyles.link} href={item.website} target="_blank" rel="noopener noreferrer">
                                                {item.company}
                                            </a>
                                        ) : item.company || '—'}
                                        {item.is_first_for_company ? null : <span className={localStyles.tag}>Not first</span>}
                                    </td>
                                    <td>{item.relationship || '—'}</td>
                                    <td>{formatNeeds(item.data_needs) || '—'}</td>
                                    <td className={localStyles.nowrap}>{item.stage || '—'}</td>
                                    <td>{[item.contact_name, item.contact_role].filter(Boolean).join(', ') || '—'}</td>
                                    <td>
                                        <div className={localStyles.clamp} title={item.notes || ''}>{item.notes || '—'}</div>
                                    </td>
                                    <td className={localStyles.nowrap}>
                                        {STATUS_LABELS[item.status] || item.status}
                                        {item.admin_notes ? (
                                            <div className={`${styles.muted} ${localStyles.clamp}`} title={item.admin_notes}>{item.admin_notes}</div>
                                        ) : null}
                                    </td>
                                    <td className={localStyles.nowrap}>
                                        {item.paid_at ? (
                                            <>
                                                {formatUsd(item.payout_amount_usd)}
                                                <div className={styles.muted}>Paid {item.paid_at}</div>
                                            </>
                                        ) : <span className={styles.muted}>Unpaid</span>}
                                    </td>
                                    <td>
                                        {item.ip_address || '—'}
                                        {item.location ? <div className={styles.muted}>{item.location}</div> : null}
                                    </td>
                                    <td>
                                        <button
                                            type="button"
                                            className={styles.editButton}
                                            onClick={() => startEdit(item)}
                                        >
                                            Edit
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={COLUMN_COUNT} className={styles.noData}>
                                    {loading ? 'Loading…' : 'No referrals found'}
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </div>
            )}

            {referrals.length > 0 && !error && (
                <div className={styles.pagination}>
                    <button
                        onClick={goToPreviousPage}
                        disabled={currentPage === 1}
                        className={`${styles.pageButton} ${currentPage === 1 ? styles.disabled : ''}`}
                    >
                        Previous
                    </button>

                    <div className={styles.pageNumbers}>
                        {Array.from({length: totalPages}, (_, i) => i + 1).map((number) => {
                            if (
                                number === 1 ||
                                number === totalPages ||
                                (number >= currentPage - 2 && number <= currentPage + 2)
                            ) {
                                return (
                                    <button
                                        key={number}
                                        onClick={() => paginate(number)}
                                        className={`${styles.pageNumber} ${currentPage === number ? styles.active : ''}`}
                                    >
                                        {number}
                                    </button>
                                );
                            } else if (
                                (number === currentPage - 3 && currentPage > 4) ||
                                (number === currentPage + 3 && currentPage < totalPages - 3)
                            ) {
                                return <span key={number} className={styles.ellipsis}>...</span>;
                            }
                            return null;
                        })}
                    </div>

                    <button
                        onClick={goToNextPage}
                        disabled={currentPage === totalPages}
                        className={`${styles.pageButton} ${currentPage === totalPages ? styles.disabled : ''}`}
                    >
                        Next
                    </button>
                </div>
            )}
            {editingItem && draft ? renderEditModal() : null}
        </div>
    );
};

export default ReferralsTable;
